import * as React from 'react';

import { cn } from '../../../lib';

const Timeline = React.forwardRef<HTMLOListElement, React.HTMLAttributes<HTMLOListElement>>(
  ({ className, ...props }, ref) => (
    <ol ref={ref} className={cn('relative border-l border-border', className)} {...props} />
  ),
);
Timeline.displayName = 'Timeline';

interface TimelineItemProps extends React.LiHTMLAttributes<HTMLLIElement> {
  status?: 'done' | 'current' | 'pending';
}

const TimelineItem = React.forwardRef<HTMLLIElement, TimelineItemProps>(
  ({ className, status, ...props }, ref) => (
    <li ref={ref} className={cn('mb-10 ml-6', className)} data-status={status} {...props} />
  ),
);
TimelineItem.displayName = 'TimelineItem';

const TimelineConnector = React.forwardRef<HTMLSpanElement, React.HTMLAttributes<HTMLSpanElement>>(
  ({ className, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(
        'absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full bg-background ring-8 ring-background',
        className,
      )}
      {...props}
    />
  ),
);
TimelineConnector.displayName = 'TimelineConnector';

const TimelineIcon = React.forwardRef<HTMLSpanElement, React.HTMLAttributes<HTMLSpanElement>>(
  ({ className, children, ...props }, ref) => (
    <TimelineConnector
      ref={ref}
      className={cn('bg-primary text-primary-foreground', className)}
      {...props}
    >
      {children}
    </TimelineConnector>
  ),
);
TimelineIcon.displayName = 'TimelineIcon';

const TimelineHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center gap-4 mb-1', className)} {...props} />
  ),
);
TimelineHeader.displayName = 'TimelineHeader';

const TimelineTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('text-lg font-semibold leading-none text-foreground', className)}
    {...props}
  />
));
TimelineTitle.displayName = 'TimelineTitle';

const TimelineTime = React.forwardRef<HTMLTimeElement, React.HTMLAttributes<HTMLTimeElement>>(
  ({ className, ...props }, ref) => (
    <time
      ref={ref}
      className={cn('text-sm font-normal text-muted-foreground', className)}
      {...props}
    />
  ),
);
TimelineTime.displayName = 'TimelineTime';

const TimelineDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
));
TimelineDescription.displayName = 'TimelineDescription';

const TimelineContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn('mt-2', className)} {...props} />,
);
TimelineContent.displayName = 'TimelineContent';

export {
  Timeline,
  TimelineItem,
  TimelineConnector,
  TimelineHeader,
  TimelineTitle,
  TimelineIcon,
  TimelineDescription,
  TimelineContent,
  TimelineTime,
};
